import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  User as UserIcon,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { updateMeSchema, changePasswordSchema } from "@sportscanner/schemas";
import { apiPost, apiPut, getApiErrorCode } from "@/lib/api";
import { useMe, type MeUser } from "@/lib/me";
import { setLanguage, type AdminLang } from "@/i18n/index";

const PASSWORD_REGEX_UPPER = /[A-Z]/;
const PASSWORD_REGEX_NUMBER = /[0-9]/;

function isPasswordStrong(pw: string): boolean {
  return (
    pw.length >= 8 &&
    PASSWORD_REGEX_UPPER.test(pw) &&
    PASSWORD_REGEX_NUMBER.test(pw)
  );
}

export default function AccountPage() {
  const { t } = useTranslation();
  const { user, club, isLoading, refresh } = useMe();

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800">
          {t("account.title")}
        </h1>
        <p className="text-sm text-neutral-500 mt-1">{t("account.subtitle")}</p>
      </div>

      <ReadOnlySection user={user} clubName={club?.name ?? null} />

      <ProfileSection user={user} onSaved={refresh} />

      <EmailSection user={user} onSaved={refresh} />

      <PasswordSection />
    </div>
  );
}

function Card({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
      <header className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div>
          <h2 className="font-heading font-semibold text-neutral-800">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-neutral-500 mt-0.5">{description}</p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

function ReadOnlySection({
  user,
  clubName,
}: {
  user: MeUser;
  clubName: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Card
      icon={<UserIcon className="w-4 h-4" />}
      title={t("account.info.title")}
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <dt className="text-neutral-500">{t("account.info.role")}</dt>
          <dd className="font-medium text-neutral-800 mt-0.5">
            {t(`roles.${user.role}`)}
          </dd>
        </div>
        <div>
          <dt className="text-neutral-500">{t("account.info.club")}</dt>
          <dd className="font-medium text-neutral-800 mt-0.5">
            {clubName ?? t("account.info.noClub")}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

function ProfileSection({
  user,
  onSaved,
}: {
  user: MeUser;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [phone, setPhone] = useState(user.phone);
  const [preferredLanguage, setPreferredLanguage] = useState<AdminLang>(
    (user.preferredLanguage as AdminLang) || "sr",
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset local state when underlying user updates (e.g. after refresh).
  useEffect(() => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setPhone(user.phone);
    setPreferredLanguage((user.preferredLanguage as AdminLang) || "sr");
  }, [user.firstName, user.lastName, user.phone, user.preferredLanguage]);

  const dirty = useMemo(
    () =>
      firstName.trim() !== user.firstName ||
      lastName.trim() !== user.lastName ||
      phone.trim() !== (user.phone ?? "") ||
      preferredLanguage !== ((user.preferredLanguage as AdminLang) || "sr"),
    [firstName, lastName, phone, preferredLanguage, user],
  );

  const isValid = firstName.trim().length >= 2 && lastName.trim().length >= 2;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || !isValid || saving) return;

    const parsed = updateMeSchema.safeParse({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      preferredLanguage,
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setSaving(true);
    setErrorCode(null);

    try {
      await apiPut("/api/admin/me", parsed.data);

      // Sync the in-page language immediately on change.
      if (preferredLanguage !== i18n.language) {
        setLanguage(preferredLanguage);
      }

      await onSaved();
      toast.success(t("account.profile.savedToast"));
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      icon={<UserIcon className="w-4 h-4" />}
      title={t("account.profile.title")}
      description={t("account.profile.description")}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-sm text-neutral-600">
              {t("common.firstName")}
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
              className="rounded-lg border-neutral-200 focus:border-primary-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-sm text-neutral-600">
              {t("common.lastName")}
            </Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
              className="rounded-lg border-neutral-200 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm text-neutral-600">
            {t("common.phone")}
          </Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+381 60 123 4567"
            disabled={saving}
            className="rounded-lg border-neutral-200 focus:border-primary-500"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lang" className="text-sm text-neutral-600">
            {t("account.profile.languageLabel")}
          </Label>
          <select
            id="lang"
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value as AdminLang)}
            disabled={saving}
            className="w-full h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm focus:outline-none focus:border-primary-500"
          >
            <option value="sr">Srpski</option>
            <option value="en">English</option>
            <option value="ru">Русский</option>
          </select>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!dirty || !isValid || saving}
            className="rounded-full bg-primary-600 hover:bg-primary-700 text-white px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EmailSection({
  user,
  onSaved,
}: {
  user: MeUser;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const canEdit = user.role === "MANAGER" || user.role === "SUPERADMIN";
  const [email, setEmail] = useState(user.email);
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(user.email);
  }, [user.email]);

  const dirty = email.trim().toLowerCase() !== user.email.toLowerCase();
  const isValid = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canEdit || !dirty || !isValid || !currentPassword || saving) return;

    const parsed = updateMeSchema.safeParse({
      email: email.trim(),
      currentPassword,
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setSaving(true);
    setErrorCode(null);

    try {
      await apiPut("/api/admin/me", parsed.data);
      await onSaved();
      setCurrentPassword("");
      toast.success(t("account.email.savedToast"));
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      icon={<Mail className="w-4 h-4" />}
      title={t("account.email.title")}
      description={
        canEdit
          ? t("account.email.description")
          : t("account.email.descriptionStaff")
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm text-neutral-600">
            {t("common.email")}
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!canEdit || saving}
            className="rounded-lg border-neutral-200 focus:border-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500"
          />
        </div>

        {canEdit && dirty && (
          <div className="space-y-2">
            <Label
              htmlFor="email-current-password"
              className="text-sm text-neutral-600"
            >
              {t("account.email.currentPasswordLabel")}
            </Label>
            <div className="relative">
              <Input
                id="email-current-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t("account.email.currentPasswordPlaceholder")}
                disabled={saving}
                className="rounded-lg border-neutral-200 focus:border-primary-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!dirty || !isValid || !currentPassword || saving}
              className="rounded-full bg-primary-600 hover:bg-primary-700 text-white px-5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("common.saving")}
                </>
              ) : (
                t("account.email.submit")
              )}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const newValid = isPasswordStrong(newPassword);
  const passwordWeak = newPassword.length > 0 && !newValid;
  const passwordMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;

  const isDisabled =
    !currentPassword ||
    !newPassword ||
    !confirmPassword ||
    !newValid ||
    passwordMismatch ||
    saving;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setSaving(true);
    setErrorCode(null);

    try {
      await apiPost("/api/admin/me/password", parsed.data);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success(t("account.password.savedToast"));
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      icon={<Lock className="w-4 h-4" />}
      title={t("account.password.title")}
      description={t("account.password.description")}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        <PasswordField
          id="current-password"
          label={t("account.password.currentLabel")}
          value={currentPassword}
          onChange={setCurrentPassword}
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          disabled={saving}
          autoComplete="current-password"
        />

        <PasswordField
          id="new-password"
          label={t("account.password.newLabel")}
          value={newPassword}
          onChange={setNewPassword}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={saving}
          autoComplete="new-password"
          invalid={passwordWeak}
          hint={
            passwordWeak ? t("auth.resetPassword.passwordRules") : undefined
          }
        />

        <PasswordField
          id="confirm-password"
          label={t("account.password.confirmLabel")}
          value={confirmPassword}
          onChange={setConfirmPassword}
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          disabled={saving}
          autoComplete="new-password"
          invalid={passwordMismatch}
          hint={
            passwordMismatch
              ? t("auth.resetPassword.passwordMismatch")
              : undefined
          }
        />

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isDisabled}
            className="rounded-full bg-primary-600 hover:bg-primary-700 text-white px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("account.password.submit")
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggle,
  disabled,
  autoComplete,
  invalid,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggle: () => void;
  disabled: boolean;
  autoComplete: string;
  invalid?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm text-neutral-600">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`rounded-lg border-neutral-200 focus:border-primary-500 pr-10 ${invalid ? "border-red-400" : ""}`}
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint && <p className="text-xs text-red-500">{hint}</p>}
    </div>
  );
}
